import { useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Group,
  List,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  fetchReserveReport,
  getReserveReportXlsxUrl,
  type ReserveHoursSource,
  type ReserveReport,
  type ReserveReportCell,
} from "../api/client";
import { useReportFetch } from "../hooks/useReportFetch";
import { formatHours } from "../utils/format";

dayjs.locale("ru");

// ---------------------------------------------------------------------------
// Helpers (экспортируются для unit-тестов)
// ---------------------------------------------------------------------------

/** Границы текущего квартала — значение по умолчанию, как в привычном файле. */
export function currentQuarter(today: Date = new Date()): { from: string; to: string } {
  const d = dayjs(today);
  const firstMonth = Math.floor(d.month() / 3) * 3;
  return {
    from: d.month(firstMonth).format("YYYY-MM"),
    to: d.month(firstMonth + 2).format("YYYY-MM"),
  };
}

/** Цвет метки источника. Расчётные часы намеренно НЕ красные: это не ошибка,
 *  а обычный режим работы отчёта — красным был бы сигнал «чини это». */
export const SOURCE_COLOR: Record<ReserveHoursSource, string> = {
  fact: "green",
  plan: "gray",
  mixed: "yellow",
};

export const SOURCE_LABEL: Record<ReserveHoursSource, string> = {
  fact: "Факт",
  plan: "Расчёт",
  mixed: "Смешанный",
};

function monthTitle(monthKey: string, showYear: boolean): string {
  const d = dayjs(`${monthKey}-01`);
  return showYear ? d.format("MMMM YYYY") : d.format("MMMM");
}

function monthValue(monthKey: string | null): Date | null {
  return monthKey ? dayjs(`${monthKey}-01`).toDate() : null;
}

// ---------------------------------------------------------------------------

function HoursCell({ cell, directions }: { cell: ReserveReportCell; directions: string[] }) {
  if (cell.hours === null || cell.source === null) {
    // Месяц вне периода работы. Прочерк, а не ноль: ноль читался бы
    // как «работал и не списал ничего».
    return (
      <Tooltip label="В этом месяце не работал">
        <Text c="dimmed">—</Text>
      </Tooltip>
    );
  }
  // US-111.1: состав перечисляем в порядке справочника, а не в том, в каком
  // ключи легли в объект. Иначе тултип и колонки выгрузки называют одни и те
  // же направления в разном порядке, и сверять их глазами тяжело.
  const known = directions.filter((name) => name in cell.by_direction);
  const rest = Object.keys(cell.by_direction).filter((name) => !known.includes(name));
  const parts: [string, string][] = [...known, ...rest].map((name) => [
    name,
    cell.by_direction[name]!,
  ]);
  const tooltip =
    parts.length > 0
      ? `${SOURCE_LABEL[cell.source]}: ${parts
          .map(([name, hours]) => `${name} — ${formatHours(hours)} ч`)
          .join(", ")}`
      : `${SOURCE_LABEL[cell.source]}: направление не задано`;
  return (
    <Tooltip label={tooltip} multiline w={320}>
      <Text
        fw={cell.source === "fact" ? 500 : 400}
        c={cell.source === "plan" ? "dimmed" : undefined}
        style={{ cursor: "help" }}
      >
        {formatHours(cell.hours)}
      </Text>
    </Tooltip>
  );
}

function SourceBadges({ cells }: { cells: ReserveReportCell[] }) {
  const sources = Array.from(
    new Set(cells.map((c) => c.source).filter((s): s is ReserveHoursSource => s !== null)),
  );
  return (
    <Group gap={4} wrap="nowrap">
      {sources.map((s) => (
        <Badge key={s} size="sm" variant="light" color={SOURCE_COLOR[s]}>
          {SOURCE_LABEL[s]}
        </Badge>
      ))}
    </Group>
  );
}

export function ReserveReportView() {
  const initial = useMemo(() => currentQuarter(), []);
  const [monthFrom, setMonthFrom] = useState<string>(initial.from);
  const [monthTo, setMonthTo] = useState<string>(initial.to);

  const query = useMemo(() => ({ monthFrom, monthTo }), [monthFrom, monthTo]);
  const fetcher = useMemo(
    () => (q: { monthFrom: string; monthTo: string }) => fetchReserveReport(q.monthFrom, q.monthTo),
    [],
  );
  const { report, loading, error } = useReportFetch<typeof query, ReserveReport>(query, fetcher);

  const showYear = useMemo(
    () => (report ? new Set(report.months.map((m) => m.slice(0, 4))).size > 1 : false),
    [report],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Group align="flex-end" gap="sm">
          <MonthPickerInput
            label="С месяца"
            valueFormat="MMMM YYYY"
            value={monthValue(monthFrom)}
            onChange={(d) => d && setMonthFrom(dayjs(d).format("YYYY-MM"))}
            w={190}
          />
          <MonthPickerInput
            label="По месяц"
            valueFormat="MMMM YYYY"
            value={monthValue(monthTo)}
            onChange={(d) => d && setMonthTo(dayjs(d).format("YYYY-MM"))}
            w={190}
          />
        </Group>
        <Anchor
          href={getReserveReportXlsxUrl(monthFrom, monthTo)}
          download
          aria-disabled={loading}
          style={loading ? { pointerEvents: "none", opacity: 0.5 } : undefined}
        >
          Экспорт в Excel
        </Anchor>
      </Group>

      <Alert color="blue" variant="light" title="Что показывает этот отчёт">
        Отчёт нужен, когда выгрузка в 1С не прошла, а часы надо провести руками, поэтому он строится
        независимо от того, сданы ли таймшиты. Где сотрудник списался — показаны его фактические
        часы; где не списался — норма к проведению (месяц за вычетом подтверждённых отсутствий),
        разложенная по долям из карточки; где списался частично — факт, добитый до нормы. Источник
        подписан у каждой строки, а состав по направлениям виден при наведении на число.
      </Alert>

      {error && (
        <Alert color="red" title="Не удалось построить отчёт">
          {error}
        </Alert>
      )}

      {loading && <Loader size="sm" />}

      {report && report.warnings.length > 0 && (
        <Alert color="yellow" variant="light" title="На что обратить внимание">
          <List size="sm">
            {report.warnings.map((w) => (
              <List.Item key={w}>{w}</List.Item>
            ))}
          </List>
        </Alert>
      )}

      {report && report.rows.length === 0 && !loading && (
        <Text c="dimmed">За выбранный период нет сотрудников — проверьте период.</Text>
      )}

      {report && report.rows.length > 0 && (
        <ScrollArea>
          <Table striped highlightOnHover withTableBorder miw={900}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th colSpan={3} />
                {report.months.map((m) => [
                  <Table.Th key={`norm-${m}`} style={{ textAlign: "right" }}>
                    <Text size="xs" c="dimmed" title="Норма месяца на полную ставку">
                      {formatHours(report.month_norms[m] ?? "0")}
                    </Text>
                  </Table.Th>,
                  <Table.Th key={`norm-${m}-absence`} />,
                ])}
                <Table.Th colSpan={3} />
              </Table.Tr>
              <Table.Tr>
                {/* US-111.1: слева только то, что читают при переносе часов
                    в 1С. Табельный номер и дата приёма — в хвост, как и в
                    выгрузке: экран и файл должны читаться одинаково, иначе
                    сверять их глазами мучительно. */}
                <Table.Th>Куда списываем</Table.Th>
                <Table.Th>Сотрудник</Table.Th>
                <Table.Th>Ставка</Table.Th>
                {report.months.map((m) => [
                  <Table.Th key={m} style={{ textAlign: "right" }}>
                    {monthTitle(m, showYear)}
                  </Table.Th>,
                  /* ⚠️ Справочная колонка, в часы месяца НЕ входит: это дни
                     отпуска и больничного, которые в 1С начисляются отдельно.
                     Без неё непонятно, почему норма к проведению ниже
                     календарной, — вопрос всплывает на каждой сверке. */
                  <Table.Th key={`${m}-absence`} style={{ textAlign: "right" }}>
                    отсутствия
                  </Table.Th>,
                ])}
                <Table.Th>Табельный номер</Table.Th>
                <Table.Th>Дата приёма</Table.Th>
                <Table.Th>Источник</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.rows.map((row) => (
                <Table.Tr key={row.employee.id}>
                  <Table.Td>
                    {row.where === "—" ? (
                      <Tooltip label="Распределение затрат не задано в карточке сотрудника">
                        <Text c="orange">не задано</Text>
                      </Tooltip>
                    ) : (
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm">{row.where}</Text>
                        {row.needs_rule && (
                          // Правило есть, но покрывает не весь период: часть
                          // месяцев отнести некуда, и метка нужна так же,
                          // как при полном отсутствии правила.
                          <Tooltip label="В части месяцев периода правило не действует — эти часы отнести некуда">
                            <Text c="orange" size="sm" style={{ whiteSpace: "nowrap" }}>
                              не на весь период
                            </Text>
                          </Tooltip>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>{row.employee.full_name}</Table.Td>
                  {/* US-113 (Р8): текст с сервера — «0,50» или куски с границами
                      при переводе внутри периода. Один формат с выгрузкой: их
                      сверяют глазами. */}
                  <Table.Td>{row.rate_display}</Table.Td>
                  {row.cells.map((cell) => [
                    <Table.Td key={cell.month} style={{ textAlign: "right" }}>
                      <HoursCell cell={cell} directions={report.directions} />
                    </Table.Td>,
                    <Table.Td key={`${cell.month}-absence`} style={{ textAlign: "right" }}>
                      {cell.absence_hours && Number(cell.absence_hours) > 0 ? (
                        <Text size="sm" c="dimmed">
                          {formatHours(cell.absence_hours)}
                        </Text>
                      ) : null}
                    </Table.Td>,
                  ])}
                  <Table.Td>{row.employee.personnel_number || <Text c="dimmed">—</Text>}</Table.Td>
                  <Table.Td>
                    {row.hire_date ? dayjs(row.hire_date).format("DD.MM.YYYY") : ""}
                  </Table.Td>
                  <Table.Td>
                    <SourceBadges cells={row.cells} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Stack>
  );
}

export default ReserveReportView;
